import React, { useEffect, useState, useCallback, useMemo } from 'react';
import '../super-admin.css';
import { 
  Typography, Card, List, Tag, Button, Input, message, Row, Col, 
  Select, DatePicker, Statistic, Collapse, Tooltip
} from 'antd';
import { 
  UserOutlined, GlobalOutlined, ShoppingCartOutlined, 
  SecurityScanOutlined, InteractionOutlined, LoginOutlined, 
  ReloadOutlined, FilterOutlined, EyeOutlined,
  MobileOutlined, DesktopOutlined, TabletOutlined, EnvironmentOutlined
} from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

// Category configurations with icons and colors
const categoryConfig = {
  visitor_session: {
    name: 'Visitor & Session',
    icon: <EyeOutlined />,
    color: '#3730a3',
    bgColor: '#3730a322'
  },
  user_interaction: {
    name: 'User Interactions', 
    icon: <InteractionOutlined />,
    color: '#059669',
    bgColor: '#05966922'
  },
  authentication: {
    name: 'Authentication',
    icon: <LoginOutlined />,
    color: '#dc2626',
    bgColor: '#dc262622'
  },
  ecommerce: {
    name: 'E-commerce',
    icon: <ShoppingCartOutlined />,
    color: '#d97706',
    bgColor: '#d9770622'
  },
  security_system: {
    name: 'Security & System',
    icon: <SecurityScanOutlined />,
    color: '#7c3aed',
    bgColor: '#7c3aed22'
  }
};

const getDeviceIcon = (deviceType) => {
  switch(deviceType?.toLowerCase()) {
    case 'mobile': return <MobileOutlined />;
    case 'tablet': return <TabletOutlined />;
    case 'desktop': 
    default: return <DesktopOutlined />;
  }
};

const ActivityLogs = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [industryCounts, setIndustryCounts] = useState({});

  // Load users with their website activities
  const loadUserActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        offset: '0'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (industryFilter) params.append('industry', industryFilter);
      if (dateRange && dateRange[0]) params.append('from_date', dateRange[0].toISOString());
      if (dateRange && dateRange[1]) params.append('to_date', dateRange[1].toISOString());
      
      const res = await fetch(`${apiBase}/admin/website-activity?${params}`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load user website activities');
      const data = await res.json();
      
      setUsers(data.users || []);
    } catch (err) {
      message.error(err.message || 'Error loading user website activities');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, eventTypeFilter, industryFilter, dateRange]);

  // Load activity statistics
  const loadStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange && dateRange[0]) params.append('from_date', dateRange[0].toISOString());
      if (dateRange && dateRange[1]) params.append('to_date', dateRange[1].toISOString());
      
      const res = await fetch(`${apiBase}/admin/website-activity/stats?${params}`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load activity statistics');
      const data = await res.json();
      
      setStats(data);
    } catch (err) {
      console.error('Error loading activity statistics:', err);
    }
  }, [dateRange]);

  // Load authoritative counts from backend (declare before use)
  const loadIndustryCounts = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/industry-website-counts`, {
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to load industry counts');
      const data = await res.json();
      const map = {};
      (data.items || []).forEach(row => {
        if (row.industry) map[row.industry] = Number(row.websites_count || 0);
      });
      setIndustryCounts(map);
    } catch (e) {
      // silently fallback to computed counts
    }
  }, []);


  useEffect(() => {
    loadUserActivities();
    loadStats();
    loadIndustryCounts();
  }, [loadUserActivities, loadStats, loadIndustryCounts]);

  const handleRefresh = () => {
    loadUserActivities();
    loadStats();
    loadIndustryCounts();
  };

  // Industries list and computed stats
  const industries = useMemo(() => ([
    { key: 'E-commerce', name: 'E-commerce' },
    { key: 'Non-profit organization', name: 'Non-profit' },
    { key: 'SaaS product / web app', name: 'SaaS / Web App' },
    { key: 'Insurance company', name: 'Insurance' },
    { key: 'Digital marketing agency', name: 'Marketing Agency' },
    { key: 'SME or small business', name: 'SME / Small Biz' },
    { key: 'University or educational institution', name: 'University / Education' }
  ]), []);

  const industryWebsiteCounts = useMemo(() => {
    const maps = {};
    industries.forEach(i => { maps[i.key] = new Set(); });
    const addUrl = (set, url) => {
      if (!url) return;
      try {
        // Normalize by hostname to avoid duplicates due to paths
        const u = new URL(url, url.startsWith('http') ? undefined : 'http://'+url);
        set.add(u.hostname.toLowerCase());
      } catch {
        // Fallback: add raw string
        set.add(String(url).toLowerCase());
      }
    };
    (users || []).forEach(u => {
      const ind = u.industry;
      if (!ind || !(ind in maps)) return;
      // Include business website if present
      addUrl(maps[ind], u.business_website);
      // Include managed websites from backend response
      (u.business_websites || []).forEach(w => addUrl(maps[ind], w));
    });
    const counts = {};
    industries.forEach(i => { counts[i.key] = maps[i.key].size; });
    return counts;
  }, [users, industries]);

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 24 }}>Website Activity Tracking</Title>

      {/* Statistics Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {Object.entries(categoryConfig).filter(([key]) => key !== 'ecommerce').map(([key, config]) => {
          const categoryStats = stats[key] || [];
          const totalCount = categoryStats.reduce((sum, stat) => sum + stat.count, 0);
          
          return (
            <Col xs={12} sm={8} lg={4} xl={4} key={key}>
              <Card hoverable style={{ borderRadius: 14 }}>
                <Statistic
                  title={config.name}
                  value={totalCount}
                  prefix={React.cloneElement(config.icon, { style: { color: config.color } })}
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Industry Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {industries.map((ind, idx) => {
          // choose an icon/color from existing imports
          const iconMap = [
            { icon: <ShoppingCartOutlined />, color: '#d97706' }, // E-commerce
            { icon: <UserOutlined />, color: '#3b82f6' }, // Non-profit
            { icon: <GlobalOutlined />, color: '#7c3aed' }, // SaaS
            { icon: <SecurityScanOutlined />, color: '#22c55e' }, // Insurance
            { icon: <InteractionOutlined />, color: '#f97316' }, // Marketing
            { icon: <GlobalOutlined />, color: '#0ea5e9' }, // SME
            { icon: <UserOutlined />, color: '#a855f7' } // University
          ];
          const cfg = iconMap[idx] || iconMap[0];
          const count = (industryCounts[ind.key] !== undefined ? industryCounts[ind.key] : industryWebsiteCounts[ind.key]) || 0;
          return (
            <Col xs={12} sm={8} lg={6} xl={6} key={ind.key}>
              <Card hoverable style={{ borderRadius: 14 }}>
                <Statistic
                  title={ind.name}
                  value={count}
                  prefix={React.cloneElement(cfg.icon, { style: { color: cfg.color } })}
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Main Content Card */}
      <Card 
        hoverable 
        style={{ borderRadius: 14 }} 
        bodyStyle={{ padding: 20 }}
        loading={loading}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ 
              width: 44, 
              height: 44, 
              borderRadius: 10, 
              background: '#3730a322', 
              color: '#3730a3', 
              display: 'grid', 
              placeItems: 'center', 
              fontSize: 20 
            }}>
              <GlobalOutlined />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Website Activity Logs</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Comprehensive website tracking and analytics</div>
            </div>
          </div>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          flexWrap: 'wrap',
          padding: '12px 16px', 
          background: '#f8fafc', 
          borderRadius: 8, 
          marginBottom: 16 
        }}>
          <Input
            placeholder="Search activities, URLs, events..."
            prefix={<FilterOutlined />}
            style={{ minWidth: 250, flex: 1 }}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onPressEnter={loadUserActivities}
          />
          <Select
            placeholder="Filter by category"
            style={{ minWidth: 150 }}
            value={categoryFilter}
            onChange={setCategoryFilter}
            allowClear
          >
            {Object.entries(categoryConfig).map(([key, config]) => (
              <Select.Option key={key} value={key}>{config.name}</Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by event type"
            style={{ minWidth: 150 }}
            value={eventTypeFilter}
            onChange={setEventTypeFilter}
            allowClear
          >
            <Select.Option value="page_visit">Page Visit</Select.Option>
            <Select.Option value="button_click">Button Click</Select.Option>
            <Select.Option value="form_submitted">Form Submitted</Select.Option>
            <Select.Option value="login_successful">Login Success</Select.Option>
            <Select.Option value="product_viewed">Product Viewed</Select.Option>
            <Select.Option value="checkout_completed">Checkout Completed</Select.Option>
          </Select>
          <Select
            placeholder="Filter by industry"
            style={{ minWidth: 220 }}
            value={industryFilter}
            onChange={setIndustryFilter}
            allowClear
          >
            <Select.Option value="E-commerce">E-commerce</Select.Option>
            <Select.Option value="Non-profit organization">Non-profit organization</Select.Option>
            <Select.Option value="SaaS product / web app">SaaS product / web app</Select.Option>
            <Select.Option value="Insurance company">Insurance company</Select.Option>
            <Select.Option value="Digital marketing agency">Digital marketing agency</Select.Option>
            <Select.Option value="SME or small business">SME or small business</Select.Option>
            <Select.Option value="University or educational institution">University or educational institution</Select.Option>
          </Select>
          <RangePicker
            style={{ minWidth: 250 }}
            value={dateRange}
            onChange={setDateRange}
            showTime
          />
        </div>

        {/* Users and Their Website Activities */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          {users.length === 0 && !loading ? (
            <div style={{ 
              padding: 32, 
              textAlign: 'center',
              color: '#64748b' 
            }}>
              <UserOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
              <div>No users with website activities found</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Website activities will appear here when users interact with their websites
              </div>
            </div>
          ) : (
            <Collapse 
              ghost 
              expandIconPosition="end"
              style={{ background: 'white' }}
            >
              {users.map((user, index) => (
                <Panel
                  key={user.user_id}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                      <div style={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: 10, 
                        background: '#3730a3', 
                        color: 'white', 
                        display: 'grid', 
                        placeItems: 'center', 
                        fontSize: 16,
                        fontWeight: 600
                      }}>
                        {user.user_name ? user.user_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                            {user.user_name || 'Unknown User'}
                          </span>
                          {user.company_name && (
                            <Tag color="blue" style={{ margin: 0 }}>
                              {user.company_name}
                            </Tag>
                          )}
                          {user.industry && (
                            <Tag color="geekblue" style={{ margin: 0 }}>
                              {user.industry}
                            </Tag>
                          )}
                          <Tag color="purple" style={{ margin: 0 }}>
                            {user.activity_count} activities
                          </Tag>
                        </div>
                        <div style={{ color: '#64748b', fontSize: 13 }}>
                          {user.user_email}
                          {user.business_website && (
                            <span> • {user.business_website}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                  style={{ 
                    borderBottom: index < users.length - 1 ? '1px solid #f1f5f9' : 'none',
                    margin: 0
                  }}
                >
                  <div style={{ paddingLeft: 52 }}>
                    {/* Website Info */}
                    {user.business_website && (
                      <div style={{ 
                        background: '#f8fafc', 
                        padding: 16, 
                        borderRadius: 8, 
                        marginBottom: 16 
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                          <GlobalOutlined style={{ marginRight: 8 }} />
                          Website Information
                        </div>
                        <div style={{ fontSize: 14, color: '#1f2937' }}>
                          <a 
                            href={user.business_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#2563eb', textDecoration: 'none' }}
                          >
                            {user.business_website}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Activities grouped by category */}
                    {user.activities.length === 0 ? (
                      <div style={{ 
                        padding: 16, 
                        textAlign: 'center', 
                        color: '#64748b',
                        background: '#f9fafb',
                        borderRadius: 8
                      }}>
                        <GlobalOutlined style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }} />
                        <div>No activities recorded for this website</div>
                      </div>
                    ) : (
                      (() => {
                        // Group activities by category
                        const activitiesByCategory = user.activities.reduce((acc, activity) => {
                          if (!acc[activity.category]) {
                            acc[activity.category] = [];
                          }
                          acc[activity.category].push(activity);
                          return acc;
                        }, {});

                        return Object.entries(activitiesByCategory).map(([category, categoryActivities]) => {
                          const config = categoryConfig[category] || { 
                            name: category, 
                            icon: <GlobalOutlined />, 
                            color: '#6b7280',
                            bgColor: '#6b728022'
                          };

                          return (
                            <div key={category} style={{ marginBottom: 16 }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 8, 
                                marginBottom: 12,
                                padding: '8px 12px',
                                background: config.bgColor,
                                borderRadius: 6
                              }}>
                                <div style={{ color: config.color, fontSize: 16 }}>
                                  {config.icon}
                                </div>
                                <span style={{ fontSize: 14, fontWeight: 600, color: config.color }}>
                                  {config.name} ({categoryActivities.length})
                                </span>
                              </div>
                              
                              <List
                                size="small"
                                dataSource={categoryActivities}
                                renderItem={(activity) => (
                                  <List.Item style={{ 
                                    padding: '8px 12px',
                                    background: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    marginBottom: 6
                                  }}>
                                    <div style={{ width: '100%' }}>
                                      {/* Activity Header */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <Tag color={config.color} size="small" style={{ margin: 0 }}>
                                          {activity.event_type.replace(/_/g, ' ').toUpperCase()}
                                        </Tag>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                                          {new Date(activity.created_at).toLocaleString()}
                                        </span>
                                        {activity.device_type && (
                                          <Tooltip title={`${activity.device_type} • ${activity.browser} • ${activity.os}`}>
                                            <div style={{ color: '#6b7280', fontSize: 12 }}>
                                              {getDeviceIcon(activity.device_type)}
                                            </div>
                                          </Tooltip>
                                        )}
                                      </div>

                                      {/* Activity Details */}
                                      <div style={{ fontSize: 12, color: '#374151' }}>
                                        {activity.page_url && (
                                          <div>📄 {activity.page_url}</div>
                                        )}
                                        {activity.location_country && (
                                          <div>
                                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                                            {activity.location_city}, {activity.location_country}
                                          </div>
                                        )}
                                        {activity.event_data && Object.keys(activity.event_data).length > 0 && (
                                          <div style={{ 
                                            background: '#f8fafc', 
                                            padding: 4, 
                                            borderRadius: 3, 
                                            marginTop: 4,
                                            fontSize: 11
                                          }}>
                                            {JSON.stringify(activity.event_data)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </List.Item>
                                )}
                              />
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                </Panel>
              ))}
            </Collapse>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ActivityLogs;
